import { BrandPortalLayout } from "@faire/prototype-playground/components/shared";
import { LiveToolbar } from "@faire/prototype-playground/platform/components/toolbar/LiveToolbar";
import type { WizardImageSlot } from "@faire/prototype-playground/workspace/yz/spe-ai-assisted-flow-mock/MockSpeImagesGrid";
import { Button } from "@faire/slate/components/button";
import { IconButton } from "@faire/slate/components/icon-button";
import { Tag } from "@faire/slate/components/tag";
import { TextArea } from "@faire/slate/components/text-area";
import { TextInput } from "@faire/slate/components/text-input";
import { Core } from "@faire/slate/foundation/color";
import { ChevronLeft } from "@faire/slate-icons/ChevronLeft";
import { HorizontalEllipsis } from "@faire/slate-icons/HorizontalEllipsis";
import { Column, Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { LE_BON_SHOPPE_BRAND } from "../../../pages/new-cc-homepage-banner/leBonShoppeProducts";

import {
  buildVariantRowsFromImages,
  cloneImageSnapshot,
} from "./shared/buildVariantRows";
import {
  AI_FIELD_DELAY_MS,
  INLINE_SPE_COPY,
  MOCK_VARIANT_PREFILL,
  OPTIONS_LOAD_MS,
} from "./shared/inlineSpeCopy";
import { InlineSpeImagesGrid } from "./shared/InlineSpeImagesGrid";
import {
  type AiFieldStatus,
  MockAiAssistedField,
} from "./shared/MockAiAssistedField";
import {
  MockProductOptionsSection,
  type OptionsSelection,
} from "./shared/MockProductOptionsSection";
import {
  createEmptyVariantFieldMap,
  type MockVariantRow,
} from "./shared/variantTable";
import { prototypeMeta } from "./meta";

const prefillForRows = (rows: MockVariantRow[]) => ({
  wholesale: Object.fromEntries(
    rows.map((r) => [r.key, MOCK_VARIANT_PREFILL.wholesale])
  ),
  retail: Object.fromEntries(
    rows.map((r) => [r.key, MOCK_VARIANT_PREFILL.retail])
  ),
  onHand: Object.fromEntries(
    rows.map((r) => [r.key, MOCK_VARIANT_PREFILL.onHand])
  ),
});
const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const SKELETON_SECTIONS = [
  { titleWidth: 140, subtitleWidth: 360, fieldHeights: [56, 56] },
  { titleWidth: 110, subtitleWidth: 300, fieldHeights: [96] },
  { titleWidth: 180, subtitleWidth: 420, fieldHeights: [56, 56, 120] },
] as const;

const sectionCardClass =
  "flex w-full flex-col gap-[var(--slate-dimensions-6)] rounded-[var(--slate-radius-container-default)] border border-[var(--slate-color-border-muted)] bg-[var(--slate-color-surface-primary)] p-[var(--slate-dimensions-10)]";

const SpeInlineSpeMockPage: React.FC = () => {
  const [images, setImages] = useState<WizardImageSlot[]>([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [titleStatus, setTitleStatus] = useState<AiFieldStatus>("idle");
  const [descStatus, setDescStatus] = useState<AiFieldStatus>("idle");
  const titleGenPass = useRef(0);

  const [optionsSelection, setOptionsSelection] =
    useState<OptionsSelection>(null);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [showOptionsAi, setShowOptionsAi] = useState(false);
  const [variantRows, setVariantRows] = useState<MockVariantRow[]>([]);

  const [variantSku, setVariantSku] = useState<Record<string, string>>({});
  const [variantWholesale, setVariantWholesale] = useState<
    Record<string, string>
  >({});
  const [variantRetail, setVariantRetail] = useState<Record<string, string>>(
    {}
  );
  const [variantOnHand, setVariantOnHand] = useState<Record<string, string>>(
    {}
  );

  const genEpochRef = useRef(0);
  const prevImageCountRef = useRef(0);
  const hasInitialOptionsGeneratedRef = useRef(false);

  const hasImages = images.length > 0;

  const applyOptionsFromImageSnapshot = useCallback(
    (snapshot: WizardImageSlot[]) => {
      const rows = buildVariantRowsFromImages(snapshot);
      const prefilled = prefillForRows(rows);
      setVariantRows(rows);
      setVariantSku(createEmptyVariantFieldMap(rows));
      setVariantWholesale(prefilled.wholesale);
      setVariantRetail(prefilled.retail);
      setVariantOnHand(prefilled.onHand);
      setOptionsSelection("has");
    },
    []
  );

  const runOptionsRegeneration = useCallback(
    async (epoch: number, snapshot: WizardImageSlot[]) => {
      setOptionsLoading(true);
      setShowOptionsAi(false);

      await delay(OPTIONS_LOAD_MS);
      if (epoch !== genEpochRef.current) {
        return;
      }

      applyOptionsFromImageSnapshot(snapshot);
      setOptionsLoading(false);
      setShowOptionsAi(true);
    },
    [applyOptionsFromImageSnapshot]
  );

  const runFullAiGeneration = useCallback(
    async (epoch: number, imageSnapshot: WizardImageSlot[]) => {
      setTitleStatus("pending");
      setDescStatus("pending");
      setOptionsLoading(true);
      setShowOptionsAi(false);

      const titlePromise = (async () => {
        await delay(AI_FIELD_DELAY_MS);
        if (epoch !== genEpochRef.current) {
          return;
        }
        titleGenPass.current += 1;
        setTitle("Oversized mineral-wash pullover — olive");
        setTitleStatus("ready");
      })();

      const descPromise = (async () => {
        await delay(AI_FIELD_DELAY_MS);
        if (epoch !== genEpochRef.current) {
          return;
        }
        setDescription(
          "Relaxed dropped-shoulder fit with ribbed collar and cuffs. Washed finish for a soft, broken-in feel. Pairs with light-wash denim."
        );
        setDescStatus("ready");
      })();

      await Promise.all([titlePromise, descPromise]);

      await delay(OPTIONS_LOAD_MS);
      if (epoch !== genEpochRef.current) {
        return;
      }

      applyOptionsFromImageSnapshot(imageSnapshot);
      setOptionsLoading(false);
      setShowOptionsAi(true);
    },
    [applyOptionsFromImageSnapshot]
  );

  useEffect(() => {
    document.title = prototypeMeta.name;
  }, []);

  useEffect(() => {
    const prev = prevImageCountRef.current;
    const next = images.length;
    prevImageCountRef.current = next;

    if (prev === 0 && next > 0 && !hasInitialOptionsGeneratedRef.current) {
      hasInitialOptionsGeneratedRef.current = true;
      genEpochRef.current += 1;
      const epoch = genEpochRef.current;
      void runFullAiGeneration(epoch, cloneImageSnapshot(images));
    }
  }, [images, runFullAiGeneration]);

  const runTitleGeneration = useCallback(async (pass: number) => {
    setTitleStatus("pending");
    await delay(AI_FIELD_DELAY_MS);
    if (titleGenPass.current !== pass) {
      return;
    }
    setTitle("Vintage acid-wash oversized crewneck sweatshirt");
    setTitleStatus("ready");
  }, []);

  const runDescGeneration = useCallback(async () => {
    setDescStatus("pending");
    await delay(AI_FIELD_DELAY_MS);
    setDescription(
      "Relaxed dropped-shoulder fit with ribbed collar and cuffs. Washed finish for a soft, broken-in feel. Pairs with light-wash denim."
    );
    setDescStatus("ready");
  }, []);

  const handleRegenerateTitle = useCallback(() => {
    if (!hasImages) {
      return;
    }
    titleGenPass.current += 1;
    void runTitleGeneration(titleGenPass.current);
  }, [hasImages, runTitleGeneration]);

  const handleRegenerateDescription = useCallback(() => {
    if (!hasImages) {
      return;
    }
    void runDescGeneration();
  }, [hasImages, runDescGeneration]);

  const handleRegenerateOptionsFromImages = useCallback(() => {
    if (images.length === 0) {
      return;
    }
    genEpochRef.current += 1;
    const epoch = genEpochRef.current;
    void runOptionsRegeneration(epoch, cloneImageSnapshot(images));
  }, [images, runOptionsRegeneration]);

  const headerTitle = title.trim() || "New product";

  return (
    <>
      <LiveToolbar />
      <BrandPortalLayout
        activeItem="Products"
        avatarUrl={LE_BON_SHOPPE_BRAND.avatarUrl}
        initials="LB"
        navLinks={{ Home: "#", Products: "#" }}
      >
        <div className="bg-fs-surface-primary flex min-h-[100cqh] flex-col">
          <div className="border-fs-border-muted w-full border-b p-[var(--slate-dimensions-10)]">
            <Button
              variant="plain"
              size="small"
              iconProps={{ Component: ChevronLeft, position: "start" }}
            >
              All products
            </Button>
            <Spacer size="8x" />
            <Row align="flex-start" justify="space-between" gap="8x">
              <Column gap="2x">
                <h1 className="type-fs-display-s-sans-semibold text-fs-text-primary m-0">
                  {headerTitle}
                </h1>
                <Row align="center" gap="4x">
                  <Tag label="Draft" variant="neutral" />
                  <span className="type-fs-label text-fs-text-subdued">
                    Created May 19, 2026
                  </span>
                </Row>
              </Column>
              <Row align="center" gap="4x">
                <IconButton
                  variant="secondary"
                  size="small"
                  shape="square"
                  Icon={HorizontalEllipsis}
                  aria-label="More actions"
                />
                <Button variant="secondary" size="small">
                  Preview
                </Button>
                <Button variant="secondary" size="small">
                  Save draft
                </Button>
                <Button variant="primary" size="small">
                  Publish
                </Button>
              </Row>
            </Row>
          </div>

          <div className="mx-auto flex w-full max-w-[1040px] flex-1 flex-col gap-[var(--slate-dimensions-8)] py-[var(--slate-dimensions-10)]">
            <section className={sectionCardClass}>
              <Row align="flex-start" justify="space-between" gap="8x">
                <Column gap="2x" className="max-w-[640px]">
                  <Typography variant="subheadingSansMedium">
                    {INLINE_SPE_COPY.imagesHeading}
                  </Typography>
                  <Typography
                    variant="paragraphSansRegular"
                    color={Core.text.subdued}
                  >
                    {INLINE_SPE_COPY.imagesSubheading}
                  </Typography>
                </Column>
                <Button variant="plain" size="small" type="button">
                  Edit images
                </Button>
              </Row>
              <InlineSpeImagesGrid images={images} onChange={setImages} />
            </section>

            <section className={sectionCardClass}>
              <Column gap="2x" className="max-w-[404px]">
                <Typography variant="subheadingSansMedium">
                  {INLINE_SPE_COPY.basicHeading}
                </Typography>
                <Typography
                  variant="paragraphSansRegular"
                  color={Core.text.subdued}
                >
                  {INLINE_SPE_COPY.basicSubheading}
                </Typography>
              </Column>

              <MockAiAssistedField
                aiEnabled={hasImages}
                status={titleStatus}
                label={INLINE_SPE_COPY.titleLabel}
                onRegenerate={handleRegenerateTitle}
                onRetry={handleRegenerateTitle}
              >
                <TextInput
                  label={hasImages ? undefined : INLINE_SPE_COPY.titleLabel}
                  placeholder={INLINE_SPE_COPY.titlePlaceholder}
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                  }}
                  maxLength={60}
                />
              </MockAiAssistedField>

              <Spacer size="6x" />

              <MockAiAssistedField
                aiEnabled={hasImages}
                status={descStatus}
                label={INLINE_SPE_COPY.descriptionLabel}
                onRegenerate={handleRegenerateDescription}
                onRetry={handleRegenerateDescription}
              >
                <TextArea
                  label={
                    hasImages ? undefined : INLINE_SPE_COPY.descriptionLabel
                  }
                  placeholder={INLINE_SPE_COPY.descriptionPlaceholder}
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                  }}
                  rows={8}
                  characterLimit={3000}
                />
              </MockAiAssistedField>
            </section>

            <section className={sectionCardClass}>
              <MockProductOptionsSection
                optionsSelection={optionsSelection}
                onOptionsSelectionChange={setOptionsSelection}
                optionsLoading={optionsLoading}
                showAiBadge={showOptionsAi}
                variantRows={variantRows}
                showVariantPhotos={showOptionsAi}
                canRegenerateOptionsFromImages={hasImages && showOptionsAi}
                onRegenerateOptionsFromImages={
                  handleRegenerateOptionsFromImages
                }
                variantSku={variantSku}
                variantWholesale={variantWholesale}
                variantRetail={variantRetail}
                variantOnHand={variantOnHand}
                onSkuChange={(key, value) => {
                  setVariantSku((prev) => ({ ...prev, [key]: value }));
                }}
                onWholesaleChange={(key, value) => {
                  setVariantWholesale((prev) => ({ ...prev, [key]: value }));
                }}
                onRetailChange={(key, value) => {
                  setVariantRetail((prev) => ({ ...prev, [key]: value }));
                }}
                onOnHandChange={(key, value) => {
                  setVariantOnHand((prev) => ({ ...prev, [key]: value }));
                }}
              />
            </section>

            {SKELETON_SECTIONS.map((section, index) => (
              <div key={index} className={sectionCardClass} aria-hidden>
                <div
                  className="bg-fs-border-muted rounded-[var(--slate-radius-component-default)]"
                  style={{ width: section.titleWidth, height: 20 }}
                />
                <div
                  className="bg-fs-surface-tertiary rounded-[var(--slate-radius-component-default)]"
                  style={{ width: section.subtitleWidth, height: 12 }}
                />
                <div className="flex max-w-[600px] flex-col gap-3">
                  {section.fieldHeights.map((h, i) => (
                    <div
                      key={i}
                      className="bg-fs-surface-tertiary w-full rounded-[var(--slate-radius-component-default)]"
                      style={{ height: h }}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </BrandPortalLayout>
    </>
  );
};

export default SpeInlineSpeMockPage;
